/* ==========================================================================
   FILE: global/components/Button.jsx
   IAMONEAI - Button Component
   Versatile button with multiple variants, sizes, and states
   ========================================================================== */

import React, { forwardRef } from 'react';
import './Button.css';

// ==========================================================================
// BUTTON COMPONENT
// ==========================================================================

/**
 * Button component with multiple variants and sizes
 * 
 * @param {Object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'|'success'|'outline'} variant - Button style
 * @param {'sm'|'md'|'lg'} size - Button size
 * @param {boolean} fullWidth - Whether button takes full width
 * @param {boolean} loading - Show loading spinner
 * @param {boolean} disabled - Disable button
 * @param {React.ReactNode} leftIcon - Icon on the left
 * @param {React.ReactNode} rightIcon - Icon on the right
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Button content
 * 
 * @example
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click Me
 * </Button>
 * 
 * <Button variant="ghost" leftIcon={<IconPlus />} loading={isLoading}>
 *   Add Item
 * </Button>
 */
const Button = forwardRef(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  children,
  type = 'button',
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  const classNames = [
    'btn',
    `btn-${variant}`,
    `btn-${size}`,
    fullWidth && 'btn-full-width',
    loading && 'btn-loading',
    isDisabled && 'btn-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classNames}
      disabled={isDisabled}
      {...props}
    >
      {/* Loading Spinner */}
      {loading && (
        <span className="btn-spinner" aria-hidden="true">
          <svg
            className="btn-spinner-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
            />
          </svg>
        </span>
      )}

      {/* Left Icon */}
      {!loading && leftIcon && (
        <span className="btn-icon btn-icon-left" aria-hidden="true">
          {leftIcon}
        </span>
      )}

      {/* Button Text */}
      <span className="btn-text">{children}</span>

      {/* Right Icon */}
      {!loading && rightIcon && (
        <span className="btn-icon btn-icon-right" aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// ==========================================================================
// ICON BUTTON COMPONENT
// ==========================================================================

/**
 * Icon-only button (square)
 * 
 * @example
 * <IconButton variant="ghost" size="md" aria-label="Close">
 *   <IconX />
 * </IconButton>
 */
export const IconButton = forwardRef(({
  variant = 'ghost',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  children,
  'aria-label': ariaLabel,
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  const classNames = [
    'btn-icon-only',
    `btn-${variant}`,
    `btn-icon-${size}`,
    loading && 'btn-loading',
    isDisabled && 'btn-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type="button"
      className={classNames}
      disabled={isDisabled}
      aria-label={ariaLabel}
      {...props}
    >
      {loading ? (
        <span className="btn-spinner" aria-hidden="true">
          <svg
            className="btn-spinner-icon"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
            />
          </svg>
        </span>
      ) : (
        children
      )}
    </button>
  );
});

IconButton.displayName = 'IconButton';

// ==========================================================================
// BUTTON GROUP COMPONENT
// ==========================================================================

/**
 * Group of buttons displayed together
 * 
 * @example
 * <ButtonGroup>
 *   <Button variant="outline">Left</Button>
 *   <Button variant="outline">Middle</Button>
 *   <Button variant="outline">Right</Button>
 * </ButtonGroup>
 */
export const ButtonGroup = ({
  children,
  className = '',
  direction = 'horizontal',
  ...props
}) => {
  const classNames = [
    'btn-group',
    `btn-group-${direction}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} role="group" {...props}>
      {children}
    </div>
  );
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export default Button;